const FONT_STORAGE_KEY = 'selectedFont';
const DEFAULT_FONT_ID = 'adwaita';

const SYSTEM_FONT_STACK =
  "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const APP_FONTS = {
  system: SYSTEM_FONT_STACK,
  adwaita: 'AdwaitaSans-Regular',
  rubik: 'Rubik',
  figtree: 'Figtree'
} as const;

export type AppFontId = keyof typeof APP_FONTS;

/**
 * Get the currently selected font ID from localStorage
 */
export function getSelectedFontId(): AppFontId {
  if (typeof window === 'undefined') return DEFAULT_FONT_ID;

  const storedFontId = localStorage.getItem(FONT_STORAGE_KEY);
  if (storedFontId && storedFontId in APP_FONTS) {
    return storedFontId as AppFontId;
  }
  return DEFAULT_FONT_ID;
}

/**
 * Save the selected font ID to localStorage
 */
export function saveSelectedFontId(fontId: AppFontId): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(FONT_STORAGE_KEY, fontId);
}

/**
 * Get font family value for a font ID
 */
export function getFontFamily(fontId: AppFontId): string {
  return APP_FONTS[fontId] ?? APP_FONTS.adwaita;
}

/**
 * Apply the selected font to the application via CSS custom property
 */
export function applyFont(fontId: AppFontId): void {
  if (typeof document === 'undefined') return;

  const fontFamily = getFontFamily(fontId);
  const cssValue = fontId === 'system' ? fontFamily : `'${fontFamily}'`;
  document.documentElement.style.setProperty('--app-font-family', cssValue);
  saveSelectedFontId(fontId);
}

/**
 * Get all available font IDs for the app font selector
 */
export function getAvailableFontIds(): AppFontId[] {
  return Object.keys(APP_FONTS) as AppFontId[];
}
